/* eslint-disable react/destructuring-assignment */
import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Avatar
} from '@material-ui/core';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import SearchIcon from '@material-ui/icons/Search';
import { motion } from 'framer-motion';
import { questionnaireApi } from '../../../api/questionnaireApi';
import useMounted from '../../../hooks/useMounted';

// Trois phases suivies, alignées sur les types de questionnaires
// (Pré-Formation, Post-Formation, Satisfaction).
const PHASES = [
  { key: 'pre', label: 'Pré', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.12)' },
  { key: 'post', label: 'Post', color: '#f97316', bg: 'rgba(249, 115, 22, 0.12)' },
  { key: 'satisfaction', label: 'Satisfaction', color: '#6366f1', bg: 'rgba(99, 102, 241, 0.12)' }
];

const getInitials = (firstname, lastname) => {
  const f = (firstname || '').trim();
  const l = (lastname || '').trim();
  return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || '?';
};

const SessionResponsesTracking = (props) => {
  const { sessionId } = props;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const mounted = useMounted();

  const getTracking = useCallback(async () => {
    setLoading(true);
    try {
      const response = await questionnaireApi.getResponsesTrackingBySession(
        sessionId,
        localStorage.getItem('accessToken')
      );
      if (mounted.current) {
        setRows(response && response.status && Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error(err);
      if (mounted.current) setRows([]);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [sessionId, mounted]);

  useEffect(() => {
    getTracking();
  }, [getTracking]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.firstname || ''} ${r.lastname || ''}`.toLowerCase().includes(q)
      || (r.email || '').toLowerCase().includes(q));
  }, [rows, search]);

  const totals = useMemo(() => {
    const acc = { pre: 0, post: 0, satisfaction: 0 };
    rows.forEach((r) => {
      PHASES.forEach((p) => {
        if (r[p.key]) acc[p.key] += 1;
      });
    });
    return acc;
  }, [rows]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card
        sx={{
          borderRadius: '20px',
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                boxShadow: '0 4px 10px -2px rgba(59, 130, 246, 0.4)'
              }}
            >
              <FactCheckRoundedIcon sx={{ color: '#fff' }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                Suivi des réponses
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={`${rows.length} ${rows.length > 1 ? 'apprenants' : 'apprenant'}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6', fontWeight: 800, borderRadius: '8px' }}
                />
                {PHASES.map((p) => (
                  <Chip
                    key={p.key}
                    label={`${p.label} ${totals[p.key]}/${rows.length}`}
                    size="small"
                    sx={{ bgcolor: p.bg, color: p.color, fontWeight: 800, borderRadius: '8px' }}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          <TextField
            size="small"
            placeholder="Rechercher un apprenant..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            sx={{ minWidth: 260, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fff' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* Contenu */}
        {loading ? (
          <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 800,
                      color: '#64748b',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      borderBottom: '2px solid #e2e8f0'
                    }}
                  >
                    Apprenant
                  </TableCell>
                  {PHASES.map((p) => (
                    <TableCell
                      key={p.key}
                      align="center"
                      sx={{
                        fontWeight: 800,
                        color: p.color,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        borderBottom: '2px solid #e2e8f0',
                        width: 140
                      }}
                    >
                      {p.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow
                    key={row.customer_id}
                    hover
                    sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #f1f5f9' } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            fontSize: '0.8rem',
                            fontWeight: 800,
                            bgcolor: 'rgba(99, 102, 241, 0.12)',
                            color: '#6366f1'
                          }}
                        >
                          {getInitials(row.firstname, row.lastname)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem', lineHeight: 1.2 }}>
                            {`${row.firstname || ''} ${row.lastname || ''}`.trim() || 'Apprenant'}
                          </Typography>
                          {row.email && (
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              {row.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    {PHASES.map((p) => (
                      <TableCell key={p.key} align="center">
                        <Tooltip title={row[p.key] ? 'A répondu' : 'Pas de réponse'} arrow>
                          <Checkbox
                            checked={!!row[p.key]}
                            readOnly
                            disableRipple
                            icon={<RadioButtonUncheckedRoundedIcon sx={{ color: '#cbd5e1' }} />}
                            checkedIcon={<CheckCircleRoundedIcon sx={{ color: p.color }} />}
                            sx={{ cursor: 'default', p: 0.5 }}
                          />
                        </Tooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={PHASES.length + 1} align="center" sx={{ py: 8, borderBottom: 'none' }}>
                      <Box sx={{ opacity: 0.6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <FactCheckRoundedIcon sx={{ fontSize: 42, color: '#94a3b8' }} />
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#475569' }}>
                          {rows.length === 0
                            ? 'Aucun apprenant inscrit pour cette session.'
                            : 'Aucun apprenant ne correspond à la recherche.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </motion.div>
  );
};

SessionResponsesTracking.propTypes = {
  sessionId: PropTypes.any.isRequired
};

export default SessionResponsesTracking;
